import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { 
  User,
  signInWithPopup, 
  signInWithRedirect,
  getRedirectResult,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  GoogleAuthProvider
} from 'firebase/auth';
import { auth } from '@/lib/firebaseAuth';
import { Handwerker, SupportedLanguage, TradeType } from '@/models/types';

// Helper to parse Firestore timestamp or Date
function parseFirestoreDate(raw: unknown): Date | null {
  if (!raw) return null;
  if (raw instanceof Date) return raw;
  if (typeof (raw as any)?.toDate === 'function') return (raw as any).toDate();
  if (typeof (raw as any)?.seconds === 'number') return new Date((raw as any).seconds * 1000);
  if (typeof raw === 'string') return new Date(raw);
  return null;
}

interface AuthContextType {
  user: User | null;
  handwerker: Handwerker | null;
  loading: boolean;
  subscriptionActive: boolean;
  subscriptionLoading: boolean;
  trialDaysLeft: number | null;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  refreshHandwerker: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

const googleProvider = new GoogleAuthProvider();

async function loadFirestore() {
  const [{ db }, firestore] = await Promise.all([
    import('@/lib/firebaseDb'),
    import('firebase/firestore'),
  ]);

  return {
    db,
    doc: firestore.doc,
    getDoc: firestore.getDoc,
    setDoc: firestore.setDoc,
    onSnapshot: firestore.onSnapshot,
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [handwerker, setHandwerker] = useState<Handwerker | null>(null);
  const [loading, setLoading] = useState(true);
  // Stripe subscription status: null = not yet checked, undefined = no doc
  const [stripeStatus, setStripeStatus] = useState<string | null | undefined>(null);
  const [stripeSubLoading, setStripeSubLoading] = useState(true);

  // Derive subscription active status from ALL sources (Stripe + profile)
  // This is a synchronous derivation — no race conditions!
  const subscriptionActive = useMemo(() => {
    // Stripe says active or trialing? → active
    if (stripeStatus && ['active', 'trialing'].includes(stripeStatus)) return true;

    // Profile says active? → active
    if (handwerker?.subscription?.status === 'active') return true;

    // Profile has valid trial? → check expiry
    if (handwerker?.subscription?.status === 'trial' && handwerker.subscription.trialEndDate) {
      const trialEnd = parseFirestoreDate(handwerker.subscription.trialEndDate);
      if (trialEnd && trialEnd > new Date()) return true;
    }

    return false;
  }, [stripeStatus, handwerker]);

  // Subscription loading: wait for Stripe check to finish AND handwerker to be loaded
  const subscriptionLoading = stripeSubLoading || (!!user && !handwerker);

  // Compute trial days remaining
  const trialDaysLeft = useMemo(() => {
    if (!handwerker?.subscription) return null;
    const sub = handwerker.subscription;
    if (sub.status === 'trial' && sub.trialEndDate) {
      const trialEnd = parseFirestoreDate(sub.trialEndDate);
      if (!trialEnd) return null;
      const diff = Math.ceil((trialEnd.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
      return Math.max(0, diff);
    }
    return null;
  }, [handwerker]);

  // Listen to Stripe subscription document ONLY (depends only on user, NOT handwerker)
  useEffect(() => {
    let unsub: (() => void) | null = null;
    let cancelled = false;

    if (!user) {
      setStripeStatus(null);
      setStripeSubLoading(false);
      return;
    }

    setStripeSubLoading(true);

    void (async () => {
      try {
        const { db, doc, onSnapshot } = await loadFirestore();

        if (cancelled) {
          return;
        }

        unsub = onSnapshot(doc(db, 'subscriptions', user.uid), (snap) => {
          if (snap.exists()) {
            setStripeStatus(snap.data().status || 'unknown');
          } else {
            setStripeStatus(undefined);
          }
          setStripeSubLoading(false);
        }, (error) => {
          console.error('Subscription listener error:', error);
          setStripeStatus(undefined);
          setStripeSubLoading(false);
        });
      } catch (error) {
        console.error('Subscription bootstrap error:', error);
        setStripeStatus(undefined);
        setStripeSubLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (unsub) {
        unsub();
      }
    };
  }, [user]);

  // Refresh handwerker profile manually (e.g., after onboarding save)
  const refreshHandwerker = useCallback(async () => {
    if (!user) return;

    try {
      const { db, doc, getDoc } = await loadFirestore();
      const docSnap = await getDoc(doc(db, 'users', user.uid));

      if (docSnap.exists()) {
        setHandwerker(docSnap.data() as Handwerker);
      }
    } catch (error) {
      console.error('Error refreshing profile:', error);
    }
  }, [user]);

  // Listen to auth state changes + realtime profile updates
  useEffect(() => {
    let profileUnsub: (() => void) | null = null;
    let cancelled = false;

    // Handle redirect result (for signInWithRedirect fallback)
    getRedirectResult(auth).catch((err) => {
      console.error('Redirect sign-in error:', err);
    });

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      // Cleanup previous profile listener
      if (profileUnsub) {
        profileUnsub();
        profileUnsub = null;
      }

      setUser(firebaseUser);
      
      if (firebaseUser) {
        // Load handwerker profile from Firestore with realtime listener
        try {
          const { db, doc, getDoc, setDoc, onSnapshot } = await loadFirestore();

          if (cancelled) {
            return;
          }

          const docRef = doc(db, 'users', firebaseUser.uid);
          const docSnap = await getDoc(docRef);
          
          if (!docSnap.exists()) {
            // Create default profile for new users (e.g., Google sign-in)
            const names = firebaseUser.displayName?.split(' ') || ['', ''];
            const defaultProfile = createDefaultHandwerker(
              firebaseUser.uid,
              firebaseUser.email || '',
              names[0] || '',
              names[names.length - 1] || ''
            );
            await setDoc(docRef, defaultProfile);
          }

          if (cancelled) {
            return;
          }

          // Start realtime listener for profile updates
          profileUnsub = onSnapshot(docRef, (snap) => {
            if (snap.exists()) {
              setHandwerker(snap.data() as Handwerker);
            }
          });
        } catch (error) {
          console.error('Error loading user profile:', error);
        }
      } else {
        setHandwerker(null);
      }
      
      setLoading(false);
    });

    return () => {
      cancelled = true;
      unsubscribe();
      if (profileUnsub) profileUnsub();
    };
  }, []);

  const signInWithGoogle = async () => {
    try {
      // Try popup first (works on most browsers), fall back to redirect (for strict COOP policies)
      try {
        await signInWithPopup(auth, googleProvider);
      } catch (popupError: any) {
        if (popupError?.code === 'auth/popup-blocked' || 
            popupError?.code === 'auth/popup-closed-by-user' ||
            popupError?.code === 'auth/cancelled-popup-request') {
          throw popupError; // Let caller handle these
        }
        // For COOP or other popup issues, fall back to redirect
        console.warn('Popup sign-in failed, using redirect:', popupError?.message);
        await signInWithRedirect(auth, googleProvider);
      }
    } catch (error) {
      console.error('Google sign-in error:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      setHandwerker(null);
    } catch (error) {
      console.error('Sign-out error:', error);
      throw error;
    }
  };

  const value = {
    user,
    handwerker,
    loading,
    subscriptionActive,
    subscriptionLoading,
    trialDaysLeft,
    signInWithGoogle,
    signOut,
    refreshHandwerker
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

function createDefaultHandwerker(
  id: string,
  email: string,
  firstName: string,
  lastName: string,
  companyName?: string,
  trade: TradeType = 'general',
  phone?: string
): Handwerker {
  const now = new Date();
  const trialEndDate = new Date();
  trialEndDate.setDate(trialEndDate.getDate() + 14); // 14-day trial
  const defaultLanguage: SupportedLanguage = 'de';
  
  return {
    id,
    email,
    firstName,
    lastName,
    companyName: companyName || `${firstName} ${lastName}`,
    trade,
    phone: phone || '',
    address: {
      street: '',
      houseNumber: '',
      postalCode: '',
      city: '',
      country: 'Deutschland'
    },
    profile: {
      services: [],
      galleryImages: [],
      website: {
        isPublished: false,
        subdomain: ''
      }
    },
    subscription: {
      status: 'trial',
      plan: 'trial',
      trialStartDate: now,
      trialEndDate: trialEndDate,
    },
    settings: {
      language: defaultLanguage,
      currency: 'EUR',
      taxRate: 19,
      hourlyRate: 65,
      travelCostPerKm: 0.50,
      workingHours: {
        monday: { enabled: true, start: '08:00', end: '17:00' },
        tuesday: { enabled: true, start: '08:00', end: '17:00' },
        wednesday: { enabled: true, start: '08:00', end: '17:00' },
        thursday: { enabled: true, start: '08:00', end: '17:00' },
        friday: { enabled: true, start: '08:00', end: '16:00' },
        saturday: { enabled: false, start: '09:00', end: '13:00' },
        sunday: { enabled: false, start: '', end: '' }
      },
      notifications: {
        email: true,
        push: true,
        whatsapp: false
      }
    },
    createdAt: now,
    updatedAt: now
  };
}
